import {priority} from '@priority-software/priority-proto-api/lib';
import {observable, action, makeObservable, computed} from 'mobx';
import {apiLocker} from './API_Locker';
import {Record} from './Record';
import {Column} from './Column';
import {OpenTableMethods} from '../../features/Form/components/Table/types';
import {getKeyByLang} from '../../features/Form/utils/custom-properties';
import {Actions, SavedSearch, SubFormsList} from '../../features/Form/types';
import {createEmptyRow} from './utils';
import {Filter} from './Filter';
import {isFormReadOnly} from '../../features/Form/actions/utils/isFormReadOnly';
import {FormHelp} from '../../_shared/Help/types';
import {SidePanelSubforms} from '../../features/Form/consts/EntityTypes';
import {LexicalEditor} from 'lexical';
import {FormMetaStoreKeys} from './enums';
import {defaultHeight} from '../../features/Form/consts/grid';
import {ISort} from './Sort';
import {isDevEnvProcess} from '../../utils/platform';
import {HelpBook} from './HelpBook';
import {SearchStore} from './SearchStore';
import {FormDesign} from '../Design/FormDesign';
import {saveTableViewColumnsDesign} from '../../features/FormDesign/service';
import {getMainCurrentForm} from '../../features/FormDesign/utils/crossAPI';
import {prepareColumnsMap} from './utils/prepareColumnsMap';
import {prepareActionsMap} from './utils/prepareActionsMap';
import {prepareSubformsMap} from './utils/prepareSubformsMap';
import {FormNames} from '../../common/staticFormNames';
import {getDname} from '../../_shared/GWTBridge/invoke/genPage';

export type FormActiveElement = HTMLInputElement | HTMLButtonElement | HTMLLinkElement | null;

export type FormDesignData = {
    tableViewColumnsDesign?: priority.netitems.form.ITableViewColumnsDesign[];
    rowViewColumnsDesign?: priority.netitems.form.IRowViewTabsDesign[];
    actionsDesign?: priority.netitems.form.IMenuDesign[];
    subformsDesign?: priority.netitems.form.IMenuDesign[];
    showPicture?: boolean;
};

export enum FormViewEnum {
    table = 'table',
    form = 'form'
}

export class Form {
    private _apiActions: Actions = [];
    private _apiColumns: priority.netitems.form.IColumn[] = []; // Raw columns data for computed properties
    private _apiSubforms: SubFormsList = []; // All subforms, including form and side-panel subforms

    level: number;
    ename: string;
    form: priority.netitems.form.IStartRs;
    sort: Array<ISort> = [];
    keyColumns: Array<Column> = [];
    parentForm: Form | null = null;
    key: string = '';
    id: string = '';
    formActiveElement: FormActiveElement;
    statuses: priority.netitems.form.IStatus[] = [];
    preventScreenRender: boolean;
    searchModeData = {};
    fastSearchQuery: string;
    originHTMLContent: string = '';
    customColumnsWidth: {[key: number]: number} = {};
    tableHeight: number;
    tableScrollOffset: {top: number; left: number} = {top: 0, left: 0};
    abortController: AbortController | undefined = undefined;
    isDevEnv = isDevEnvProcess();
    maxFetch: number;
    searchStore = new SearchStore(this);
    refreshCode: priority.netitems.form.RefreshLines = null;
    dname: string;

    @observable _htmlEditorInstance: LexicalEditor = null;
    @observable filters: Array<Filter> = [];
    @observable formHelp: FormHelp | null = null;
    @observable view: FormViewEnum = FormViewEnum.table;
    @observable collapsed: boolean = false;
    @observable HTMLContent: string = '';
    @observable currentColumnId: number;
    @observable currentRow: number;
    @observable records: Record[] = [];
    @observable searchModeLevel: number = 0;
    @observable tableAPI: OpenTableMethods;
    @observable activeSubForm: Form | null = null;
    @observable activeTabSubForm: Form | null = null;
    @observable shouldFetchMore: boolean = false;
    @observable paginationOffset: number = 0;
    @observable hasMore: boolean = false;
    @observable allRecordsCount: number = 0;
    @observable hiddenRecords: Array<Array<priority.netitems.form.IField>> = [];
    @observable helpBook = new HelpBook();
    @observable formDesign: FormDesign = null;
    @observable lockBy: string | null = null;
    @observable isFavorite: boolean = false;

    constructor(
        ename: string,
        form: priority.netitems.form.IStartRs,
        columns: priority.netitems.form.IColumnsRs,
        level: number,
        formCustomData: any = {},
        formDesignData: FormDesignData = {},
        dname: string = getDname()
    ) {
        makeObservable(this);
        this.ename = ename;
        this.form = form;
        this.level = level;
        // Handle form meta data before processing the columns
        this.handleFormMetaData(formCustomData);

        if (form.OneLine) {
            this.view = FormViewEnum.form;
        }

        this.key = Date.now().toString();
        this.id = `${ename}-level-${level}`;

        this.formDesign = new FormDesign(formDesignData, this);
        this.lockBy = form?.LockBy || null;
        this.isFavorite = form?.IsFavorite || false;

        // Store raw columns data for computed properties
        this.setColumns(columns);
        this.setRecords([]); // set default rows
        this.currentColumnId = this.columns?.[0]?.id;
        this.currentRow = 0;
        this.keyColumns = this.columns.filter(c => c.Key);
        // save the current dname - the one passed to the constructor or the one set on the app level at the time of form creation
        // when dname is changes on application level, the forms should keep the original one
        this.dname = dname;
    }

    get session(): string {
        return this.form?.Session;
    }

    get title(): string {
        return this.form?.Title;
    }

    // This is the primary cache that other properties derive from to avoid recalculating multiple times
    @computed get columnProcessingCache(): {
        columnsMap: Map<number, Column>;
        fixedColumnsNumber: number;
        visibleColumns: Column[];
        allColumns: Column[];
    } {
        const {columnsMap, fixedColumnsNumber} = prepareColumnsMap(
            this,
            this._apiColumns,
            this.formDesign?.tableColumnsDesign || []
        );
        const allColumns = [...columnsMap.values()];
        const visibleColumns = allColumns.filter(c => !c.hidden);

        return {
            columnsMap,
            fixedColumnsNumber,
            visibleColumns,
            allColumns
        };
    }

    @computed get columnsMap(): Map<number, Column> {
        return this.columnProcessingCache.columnsMap;
    }

    @computed get columns(): Column[] {
        return this.columnProcessingCache.allColumns;
    }

    @computed get visibleColumns(): Column[] {
        return this.columnProcessingCache.visibleColumns;
    }

    @computed get fixedColumnsNumber(): number {
        return this.columnProcessingCache.fixedColumnsNumber;
    }

    get currentColumn(): Column | null {
        return this.columnsMap.get(this.currentColumnId) || null;
    }

    get isReadonly(): boolean {
        return isFormReadOnly(this.form);
    }

    get isNoDelete(): boolean {
        return this.form?.Privileges === 1;
    }

    get currentRecord(): Record {
        return this.records[this.currentRow];
    }

    activeField = () => {
        return this.records[this.currentRow]?.fieldsMap.get(this.currentColumnId);
    };

    getColumnByID(id: number): Column {
        return this.columns.find(c => c.ID === id);
    }

    getColumnByName(name: string): Column {
        return this.columns.find(c => c.Name === name);
    }

    setFormActiveElement = (el: FormActiveElement) => {
        this.formActiveElement = el;
    };

    setParentForm = (parentForm: Form) => {
        this.parentForm = parentForm;

        parentForm.setSubForm(this);
    };

    setColumns = (columns: priority.netitems.form.IColumnsRs) => {
        this._apiColumns = columns?.Columns || [];
    };

    handleFormMetaData = customData => {
        const columnsWidthKey = getKeyByLang(
            this.ename,
            'CUSTOM_DATA',
            FormMetaStoreKeys.customColumnsWidth
        );
        const tableHeightKey = getKeyByLang(
            this.ename,
            'CUSTOM_DATA',
            `${FormMetaStoreKeys.tableHeight}:${this.level}`
        );

        const formViewKey = getKeyByLang(this.ename, 'CUSTOM_DATA', FormMetaStoreKeys.formView);

        if (customData) {
            if (customData[columnsWidthKey]) {
                this.customColumnsWidth = JSON.parse(customData[columnsWidthKey].Str);
            }
            if (customData[tableHeightKey]) {
                this.tableHeight = customData[tableHeightKey].Int || defaultHeight;
            }
            if (customData[formViewKey]) {
                this.view = customData[formViewKey].Str || FormViewEnum.table;
            }
        }
    };

    updateSearchModeCellValue = (val: string, colId: number, rowIndex: number) => {
        this.searchModeData = {
            ...this.searchModeData,
            [rowIndex]: {
                ...(this.searchModeData[rowIndex] || {}),
                [colId]: val
            }
        };
    };

    clearSearchModeData = () => (this.searchModeData = {});

    setAbortController = () => {
        this.abortController = new AbortController();
    };

    onCancelRs = () => {
        this.abortController.abort();
        this.abortController = undefined;
    };

    setHiddenRecordKeys = (recordKeys: Array<priority.netitems.form.IField>) => {
        this.hiddenRecords = [...this.hiddenRecords, recordKeys];
    };

    resetHiddenRecordKeys = () => {
        this.hiddenRecords = [];
    };

    @action toggleViewState = (collapsed: boolean) => {
        this.collapsed = collapsed;
    };

    @action setSubForm = (form: Form) => {
        this.activeSubForm = form;
        if (!form?.isPanelTypeSubform()) {
            this.activeTabSubForm = form;
        }
    };

    @action resetLockBy = () => {
        this.lockBy = null;
    };

    isPanelTypeSubform = () => {
        const subform = this.parentForm?._apiSubforms?.find(s => s.Ename === this.ename);
        return this.parentForm?.checkPanelType(subform);
    };

    checkPanelType = (subform: priority.netitems.form.ISubform) => {
        return (
            SidePanelSubforms.includes(subform?.Type) ||
            (subform?.Ename === FormNames.attachments && this.ename === FormNames.composeMail)
        );
    };

    get subformType(): priority.netitems.form.SubformType {
        if (!this.parentForm) {
            return null;
        }
        const subform = this.parentForm?._apiSubforms?.find(s => s.Ename === this.ename);
        return subform?.Type;
    }

    @computed get actions(): Actions {
        return prepareActionsMap(this._apiActions, this.formDesign?.actionsDesign || []);
    }

    @computed get subFormsList(): SubFormsList {
        // Contains only design-based visual subforms, excluding side-panel ones.
        return prepareSubformsMap(
            this.visibleSubformsList,
            this.formDesign?.subformsDesign || [],
            s => !this.checkPanelType(s)
        );
    }

    @computed get visibleSubformsList(): SubFormsList {
        const subformsDesign = this.formDesign?.subformsDesign || [];
        const apiHiddenSubformsSet = new Set(subformsDesign.filter(s => s.Hidden).map(s => s.Name));
        return this._apiSubforms.filter(sf => !apiHiddenSubformsSet.has(sf.Ename));
    }

    @action setFormActions = (actions: Actions = []) => {
        this._apiActions = actions;
    };

    @action setSubForms = (data: Array<priority.netitems.form.ISubform> = []) => {
        this._apiSubforms = data;
    };

    @action updateNewSearch = (search: SavedSearch) => {
        // Todo: this old logic should be rewritten
        const {savedSearches, setFormSavedSearches} = this.searchStore;
        const newSearchIndex = savedSearches.findIndex(srch => !srch.isSaved);

        const updatedSearches =
            newSearchIndex > -1
                ? savedSearches.map((srch, index) => (index === newSearchIndex ? search : srch))
                : [search, ...savedSearches];

        setFormSavedSearches(updatedSearches);
    };

    @action
    setTableAPI = (api: OpenTableMethods) => (this.tableAPI = api);

    @computed get sidePanelSubforms() {
        const subforms = {};
        this.visibleSubformsList.forEach(s => {
            if (s.Type && SidePanelSubforms.includes(s.Type)) {
                subforms[s.Type] = s.Ename;
            } else if (this.checkPanelType(s)) {
                subforms[priority.netitems.form.SubformType.Attachments] = s.Ename;
            }
        });
        return subforms;
    }

    @action setCurrentColumnId = (value: number) => {
        if (this.currentColumnId !== value) {
            this.currentColumn?.setEditMode(false);
            this.currentColumnId = value;
        }
    };

    @action setCurrentColumnIdByIndex = (index: number) => {
        const ID = this.visibleColumns[index]?.id;
        this.setCurrentColumnId(ID);
    };

    @action setCurrentRow = (rowIndex: number) => {
        this.currentRow = rowIndex;
    };

    @action updateFixedColumnsCount = async (value: number) => {
        this.formDesign.setTableColumnsDesign(
            this.formDesign.tableColumnsDesign.map((designColumn, i) => ({
                ...designColumn,
                Cover: i === value - 1 ? true : null
            }))
        );
        await saveTableViewColumnsDesign(
            this.formDesign.tableColumnsDesign,
            this.ename,
            getMainCurrentForm()?.ename
        );
    };

    @action setHTMLContent = (content: string = '') => {
        this.HTMLContent = content;
    };

    setOriginHTMLContent = (content: string = '') => {
        this.originHTMLContent = content;

        if (this.HTMLContent !== content) {
            this.setHTMLContent(content);
        }
    };

    @action
    setEditorInstance = editorInstance => {
        this._htmlEditorInstance = editorInstance;
    };

    addFilter = (filter: Filter) => {
        this.filters.push(filter);
    };

    updateFilters = (filter: Filter[] = []) => {
        this.filters = filter;
    };

    resetFilters = () => {
        this.filters = [];
        this.searchStore.setCurrentSearchId(null);
        this.resetPagination();
    };

    @action
    resetPagination = () => {
        this.paginationOffset = 0;
        this.allRecordsCount = 0;
    };

    setSort = (sort: ISort) => {
        this.sort.push(sort);
    };

    updateSort = (sort: ISort[]) => {
        this.sort = sort;
    };

    resetSort = () => {
        this.sort = [];
    };

    createEmptyRows = (count: number = 1): priority.netitems.form.IRecord[] => {
        return new Array(count).fill(null).map(() => createEmptyRow(this.columns));
    };

    setStatuses = statuses => {
        this.statuses = statuses;
    };

    @action
    updateRecordValues = (
        recordIndex: number,
        fields: priority.netitems.form.IField[] | null,
        isNew?: boolean
    ) => {
        this.records = this.records.map(record => {
            if (record.recordIndex === recordIndex) {
                record.setFields(fields);
                if (isNew !== undefined) {
                    record.isNew = isNew;
                }
            }
            return record;
        });
    };

    @action
    setRecords = (
        records: priority.netitems.form.IRecord[] = [],
        paginationOffset?: number,
        hasMoreData?: boolean
    ) => {
        this.isDevEnv && console.time(`setRecords - ${this.ename}`);
        this.records = records.map((record, index) => new Record(this, record, index));
        const lastRecord = this.records[this.records.length - 1];

        if (this.records.length === 0) {
            this.insertEmptyRecord(0);
        } else if (!lastRecord?.isNew && !hasMoreData) {
            this.insertEmptyRecord(this.records.length);
        }
        this.isDevEnv && console.timeEnd(`setRecords - ${this.ename}`);

        this.setPaginationOffset(paginationOffset, hasMoreData);
    };

    @action
    mergeRecords = (records: Record[], newIRecords: priority.netitems.form.IRecord[] = []) => {
        this.isDevEnv && console.time('setRecords');
        const newRecords = newIRecords.map(
            (record, index) => new Record(this, record, records.length + index)
        );
        this.records = [...records, ...newRecords];
        this.isDevEnv && console.timeEnd('setRecords');
    };

    @action
    resetRecords = () => {
        this.setRecords([]);
        this.setOriginHTMLContent('');
        this.activeSubForm?.resetRecords();
    };

    @action
    setSearchModeLevel = (searchModeLevel: number) => {
        if (this.searchModeLevel !== searchModeLevel) {
            this.searchModeLevel = searchModeLevel;
            this.setCurrentRow(searchModeLevel === 0 ? 0 : 1);
        }
    };

    @action
    insertEmptyRecord = (position: number, isTemporarily?: boolean) => {
        const emptyRow = this.createEmptyRows(1)[0];

        this.records = [
            ...this.records.slice(0, position + 1),
            new Record(this, emptyRow, 0, true, isTemporarily),
            ...this.records.slice(position + 1, this.records.length)
        ].map((record, i) => {
            record.setRecordIndex(i);
            return record;
        });
    };

    @action
    resetRecord = (rowInd: number) => {
        this.records[rowInd] = new Record(this, createEmptyRow(this.columns), rowInd, true);
    };

    setShouldFetchMore = (shouldFetchMore: boolean) => {
        this.shouldFetchMore = shouldFetchMore;
    };

    setFastSearchQuery = (searchQuery: string) => {
        this.fastSearchQuery = searchQuery;
    };

    getFastSearchQuery = () => {
        return this.fastSearchQuery;
    };

    isApiLock = () => {
        return apiLocker.getDataBySession(this.session)?.locked;
    };

    @action
    deleteClientRecord = (recordIndex: number) => {
        // delete record by index on client side
        this.records.splice(recordIndex, 1);
        // rearrange records indexes after deletion of a record
        this.records.map(record => {
            if (record.recordIndex > recordIndex) {
                record.setRecordIndex(record.recordIndex - 1);
            }
            return record;
        });
    };

    @action
    setPaginationOffset(paginationOffset: number = 0, hasMore: boolean = false) {
        this.paginationOffset = paginationOffset;
        this.hasMore = hasMore;
    }

    @action
    setHasMore(hasMore: boolean) {
        this.hasMore = hasMore;
    }

    @action
    setAllRecordsCount(count: number = 0) {
        this.allRecordsCount = count;
    }

    get isEmpty() {
        return this.form.Ishtml ? !this.originHTMLContent : !this.records.find(r => !r.isNew);
    }

    get isMultiComp() {
        return this.form?.MultiComp || false;
    }

    setIsFavorite = (value: boolean) => {
        this.isFavorite = value;
    };
}
